import { useCallback, useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight, Play, Pause } from "lucide-react";
import { SmartImg } from "./SmartImg";

export const Lightbox = ({ urls, index = 0, onClose, captions = null, withSlideshow = false, autoplayStart = false }) => {
  const [i, setI] = useState(index);
  const [zoom, setZoom] = useState(false);
  const [playing, setPlaying] = useState(withSlideshow && autoplayStart);
  const [origin, setOrigin] = useState({ x: 50, y: 50 });
  const [loaded, setLoaded] = useState(false);
  const [touchX, setTouchX] = useState(null);
  const go = useCallback((dir) => {
    setZoom(false);
    setLoaded(false);
    setI((v) => (v + dir + urls.length) % urls.length);
  }, [urls.length]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [go, onClose]);
  useEffect(() => {
    if (!playing || urls.length < 2) return;
    const iv = setInterval(() => go(1), 4000);
    return () => clearInterval(iv);
  }, [playing, go, urls.length]);
  useEffect(() => {
    [i - 1, i + 1].forEach((j) => {
      const u = urls[(j + urls.length) % urls.length];
      if (u) new Image().src = u;
    });
  }, [i, urls]);
  const originFrom = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: ((e.clientX - rect.left) / rect.width) * 100, y: ((e.clientY - rect.top) / rect.height) * 100 };
  };
  return (
    <div data-testid="lightbox" className="fixed inset-0 z-[3000] bg-black/95 flex items-center justify-center overflow-hidden animate-in fade-in duration-200"
      onClick={onClose}
      onTouchStart={(e) => setTouchX(e.touches[0].clientX)}
      onTouchEnd={(e) => {
        if (touchX == null || zoom) return;
        const dx = e.changedTouches[0].clientX - touchX;
        if (dx > 50) go(-1);
        else if (dx < -50) go(1);
        setTouchX(null);
      }}>
      <button data-testid="lightbox-close" onClick={onClose} aria-label="Sluiten"
        className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-[background-color] duration-200">
        <X className="w-5 h-5" />
      </button>
      {withSlideshow && urls.length > 1 && (
        <button data-testid="lightbox-play" onClick={(e) => { e.stopPropagation(); setPlaying((p) => !p); }}
          aria-label={playing ? "Pauzeer diavoorstelling" : "Start diavoorstelling"}
          className="absolute top-4 left-4 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-[background-color] duration-200">
          {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </button>
      )}
      {captions?.[i] && (
        <span data-testid="lightbox-caption" onClick={(e) => e.stopPropagation()}
          className="absolute bottom-9 left-1/2 -translate-x-1/2 max-w-[90vw] truncate bg-black/50 text-white/90 text-xs rounded-full px-3.5 py-1.5">
          {captions[i]}
        </span>
      )}
      {urls.length > 1 && (
        <>
          <button data-testid="lightbox-prev" aria-label="Vorige foto" onClick={(e) => { e.stopPropagation(); go(-1); }}
            className="absolute left-2 sm:left-5 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-[background-color] duration-200">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button data-testid="lightbox-next" aria-label="Volgende foto" onClick={(e) => { e.stopPropagation(); go(1); }}
            className="absolute right-2 sm:right-5 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-[background-color] duration-200">
            <ChevronRight className="w-5 h-5" />
          </button>
          <span data-testid="lightbox-counter" className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 text-xs font-mono">{i + 1} / {urls.length}</span>
        </>
      )}
      {!loaded && <div className="absolute w-10 h-10 rounded-full border-4 border-white/30 border-t-white animate-spin" />}
      <SmartImg src={urls[i]} alt="Reisfoto groot" data-testid="lightbox-image" draggable={false} retryLabel="Foto opnieuw laden"
        onLoad={() => setLoaded(true)} onFail={() => setLoaded(true)}
        onClick={(e) => { e.stopPropagation(); setOrigin(originFrom(e)); setZoom((z) => !z); }}
        onMouseMove={zoom ? (e) => setOrigin(originFrom(e)) : undefined}
        className={`max-w-[96vw] max-h-[92vh] object-contain select-none transition-transform duration-300 ${zoom ? "cursor-zoom-out" : "cursor-zoom-in"} ${loaded ? "opacity-100" : "opacity-0"}`}
        style={{ transform: zoom ? "scale(2.5)" : "scale(1)", transformOrigin: `${origin.x}% ${origin.y}%` }} />
    </div>
  );
};
